import { LockClosedIcon } from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';

import { cn } from '@/utils/classname';

export interface FeatureLockProps {
  /** Whether the content is locked */
  locked: boolean;
  /** Content to display when unlocked */
  children: ReactNode;
  /** Title text when locked (default: "Feature Locked") */
  title?: string;
  /** Description text or element when locked */
  description?: ReactNode;
  /** Custom icon element (default: LockClosedIcon) */
  icon?: ReactNode;
  /** Additional CSS classes for the container */
  className?: string;
  /** Optional action element (button, link, etc.) */
  action?: ReactNode;
}

/**
 * FeatureLock - Display component for gated/locked features
 *
 * Shows a themed empty state with lock icon and messaging when
 * content is restricted. Used for feature flags, subscription tiers,
 * and Praetorian-only features.
 *
 * @remarks
 * - Uses NoData-style solid background (theme-aware)
 * - Children are NOT rendered when locked (performance optimization)
 * - Supports light and dark mode via Chariot design tokens
 *
 * @example
 * ```tsx
 * <FeatureLock locked={!hasFeature}>
 *   <FeatureContent />
 * </FeatureLock>
 * ```
 */
export function FeatureLock({
  locked,
  children,
  title = 'Feature Locked',
  description = 'Contact support@praetorian.com to unlock this feature',
  icon,
  className,
  action,
}: FeatureLockProps) {
  // When unlocked: render children normally
  if (!locked) {
    return <>{children}</>;
  }

  const defaultIcon = <LockClosedIcon className="text-chariot-stroke" />;

  // When locked: show themed empty state (children NOT rendered)
  return (
    <div className="@container flex h-full w-full flex-1">
      <div
        className={cn(
          'flex w-full flex-col items-center justify-center gap-2',
          'bg-chariot-background-secondary text-chariot-text-primary',
          className
        )}
        role="status"
        aria-label={`Feature locked: ${title}`}
      >
        <div
          className={cn(
            'flex items-center justify-center',
            '[&>svg]:h-12 [&>svg]:w-auto',
            '@xl:[&>svg]:h-16',
            '@2xl:[&>svg]:h-20',
            '@3xl:[&>svg]:h-[100px]',
            '@4xl:[&>svg]:h-[120px]',
            '@5xl:[&>svg]:h-[140px]'
          )}
        >
          {icon ?? defaultIcon}
        </div>
        <h6 className={cn('text-center font-bold', 'text-base @3xl:text-lg')}>
          {title}
        </h6>
        {description && (
          <div className="text-chariot-text-secondary text-center text-sm whitespace-break-spaces">
            {description}
          </div>
        )}
        {action && <div className="mt-4">{action}</div>}
      </div>
    </div>
  );
}
